import { FormGroup, Validators } from '@angular/forms';
import { extractBankCode, extractBankName } from '../cashout-bank.helpers';

export function setupEurCashoutForm(ctx: any): void {
  setDefaultEurBankByPriority(ctx);
  updateClearJunctionAddressValidators(ctx);
}

/**
 * Asigna el banco EUR según configuration.cashout_providers_priorities del
 * cliente actual. Si no hay prioridad o match, usa el primer subwallet.
 */
export function setDefaultEurBankByPriority(ctx: any): void {
  const banks = ctx.getBanks();
  if (!banks?.length) return;

  const currencyConfig = ctx.userService.user_balance_config?.currencies?.find(
    (c: any) => (c.id ?? c.currency) === 'EUR',
  );
  const cashoutService = currencyConfig?.services?.find(
    (s: any) => s?.name === 'Cashout',
  );
  const providerServices = cashoutService?.provider_services ?? [];
  const client = getCurrentClient(ctx);
  const priorities: number[] = Array.isArray(
    client?.configuration?.cashout_providers_priorities,
  )
    ? client.configuration.cashout_providers_priorities.map((id: number) =>
        Number(id),
      )
    : [];

  let selectedBank: any = null;
  if (priorities.length && providerServices.length) {
    const prioritySet = new Set(priorities);
    const ordered = [
      ...priorities.filter((uid) =>
        providerServices.some(
          (ps: any) => Number(ps?.provider?.user_id ?? ps?.provider_id) === uid,
        ),
      ),
      ...providerServices
        .map((ps: any) => ps?.provider?.user_id ?? ps?.provider_id)
        .filter((uid: number) => !prioritySet.has(Number(uid))),
    ];
    const firstProviderId = ordered[0];
    const firstPs = providerServices.find(
      (ps: any) =>
        Number(ps?.provider?.user_id ?? ps?.provider_id) ===
        Number(firstProviderId),
    );
    const providerShortname =
      firstPs?.provider?.shortname ?? firstPs?.provider?.username ?? '';
    if (providerShortname) {
      const shortname = String(providerShortname).trim().toLowerCase();
      selectedBank = banks.find(
        (b: any) =>
          String(b?.shortname ?? '')
            .trim()
            .toLowerCase() === shortname ||
          String(b?.name ?? '')
            .trim()
            .toLowerCase() === shortname ||
          String(b?.id ?? '')
            .trim()
            .toLowerCase() === shortname,
      );
    }
  }
  if (!selectedBank) selectedBank = banks[0];

  const name = extractBankName(selectedBank);
  const code = extractBankCode(selectedBank);
  if (code) {
    ctx.newTransactionForm.controls.company_id.setValue(selectedBank);
    ctx.newTransactionForm.controls.company_name.setValue(
      name || selectedBank?.shortname || selectedBank?.name || code,
    );
  }
  updateClearJunctionAddressValidators(ctx);
}

export function setClearJunctionPayeeType(
  ctx: any,
  type: 'individual' | 'corporate',
): void {
  ctx.newTransactionForm.controls.clearjunction_payee_type.setValue(type);
}

/**
 * Lee dirección CJ desde getRawValue para no perder zip/city/street si Angular
 * excluye algún control deshabilitado del value normal.
 */
export function readClearJunctionAddressFields(ctx: any): {
  country: string;
  zip: string;
  city: string;
  street: string;
} {
  const raw = ctx.newTransactionForm.getRawValue() as {
    clearjunction_payee?: {
      address?: Record<string, unknown>;
    };
  };
  const nested: Record<string, unknown> =
    raw?.clearjunction_payee?.address ?? {};
  const pick = (v: unknown) =>
    v === null || v === undefined ? '' : String(v).trim();
  return {
    country: pick(nested['country']),
    zip: pick(nested['zip']),
    city: pick(nested['city']),
    street: pick(nested['street']),
  };
}

export function buildClearJunctionPayeeBodyForAdd(ctx: any): {
  clearjunction_payee_type: string;
  clearjunction_payee: { individual?: object; corporate?: object };
} | null {
  const payeeGroup = ctx.newTransactionForm.get('clearjunction_payee');
  if (!payeeGroup) return null;
  const address = readClearJunctionAddressFields(ctx);
  const firstname = (ctx.newTransactionForm.controls.firstname.value ?? '')
    .toString()
    .trim();
  const lastname = (ctx.newTransactionForm.controls.lastname.value ?? '')
    .toString()
    .trim();
  const type = ctx.newTransactionForm.controls.clearjunction_payee_type.value;
  if (type === 'corporate') {
    const name = [firstname, lastname].filter(Boolean).join(' ') || 'Empresa';
    return {
      clearjunction_payee_type: 'corporate',
      clearjunction_payee: {
        corporate: { name, address },
      },
    };
  }
  return {
    clearjunction_payee_type: 'individual',
    clearjunction_payee: {
      individual: {
        firstName: firstname || 'Beneficiary',
        lastName: lastname || 'Unknown',
        address,
      },
    },
  };
}

/** ClearJunction exige dirección en API externa; alineamos el form con backend. */
export function updateClearJunctionAddressValidators(ctx: any): void {
  const addrGroup = ctx.newTransactionForm.get(
    'clearjunction_payee.address',
  ) as FormGroup | null;
  if (!addrGroup) return;
  const zipFiveDigits = Validators.pattern(/^\d{5}$/);
  for (const key of ['country', 'zip', 'city', 'street'] as const) {
    const control = addrGroup.get(key);
    if (!control) continue;
    control.setValidators(
      key === 'zip'
        ? [Validators.required, zipFiveDigits]
        : [Validators.required],
    );
    control.updateValueAndValidity({ emitEvent: false });
  }
}

function getCurrentClient(ctx: any): any {
  if (ctx.auth.hasRole(ctx.roles.Admin)) {
    const clientControlValue = ctx.newTransactionForm.controls.client_id.value;
    if (clientControlValue) {
      const id =
        typeof clientControlValue === 'object'
          ? (clientControlValue.user_id ?? clientControlValue.id)
          : clientControlValue;
      return ctx.userService.users?.find(
        (c: any) => Number(c.user_id) === Number(id),
      );
    }
  }
  return ctx.auth.user ?? null;
}
